<template>
    <ModalContent
        v-if="product"
        flat-chrome
        :help-content-id="`popup-product-${current_entity_profile}`"
    >
        <template slot="title">{{ title }}</template>

        <template slot="content">
            <Info v-if="params.inventoryCreation">Ce produit sera créé comme un bien suivi en stock et ajouté immédiatement à l’inventaire.</Info>
            <div class="stitch-modal general-settings-reference product-form">
                <template v-if="!isOperator">
                    <template v-if="product.archived">
                        <Warning :elevation="1">
                            {{ $translate("archivedProduct") }}
                        </Warning>
                    </template>
                </template>
                <template v-else>
                    <Info :elevation="1">
                        {{ $translate("productNotOwned") }}
                    </Info>
                </template>

                <v-card class="stitch-modal__card" elevation="0"
                    ><v-card-text
                        ><h5 class="stitch-modal__title">
                            <v-icon size="16">mdi-shape-outline</v-icon
                            ><span>{{ $translate("labels.productType") }}</span>
                        </h5>

                        <v-row>
                            <v-col cols="12">
                                <div class="product-type-field">
                                    <v-row dense>
                                        <v-col
                                            v-for="option in productTypeChoices"
                                            :key="String(option.value)"
                                            cols="12"
                                            sm="6"
                                            class="d-flex"
                                        >
                                            <button
                                                type="button"
                                                class="product-type-option flex-grow-1"
                                                :class="{
                                                    'product-type-option--selected':
                                                        product.is_service ===
                                                        option.value,
                                                }"
                                                :disabled="
                                                    product.archived ||
                                                    isOperator
                                                "
                                                @click="
                                                    product.is_service =
                                                        option.value
                                                "
                                            >
                                                <span
                                                    class="product-type-option__title"
                                                    >{{ option.title }}</span
                                                >
                                                <span
                                                    class="product-type-option__description"
                                                    >{{
                                                        option.description
                                                    }}</span
                                                >
                                            </button>
                                        </v-col>
                                    </v-row>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card-text></v-card
                ><v-card class="stitch-modal__card" elevation="0"
                    ><v-card-text
                        ><h5 class="stitch-modal__title">
                            <v-icon size="16">mdi-package-variant-closed</v-icon
                            ><span>{{
                                $translate("productInformations")
                            }}</span>
                        </h5>

                        <v-row>
                            <v-col cols="12" :sm="product.provider ? 12 : 6">
                                <v-text-field
                                    v-model="product.name"
                                    :error-messages="product_saving_errors.name"
                                    :label="$translate('labels.name')"
                                    :placeholder="
                                        $translate('labels.namePlaceholder')
                                    "
                                    :disabled="product.archived || isOperator"
                                    required
                                    dense
                                    autofocus
                                    hide-details="auto"
                                    outlined
                                    @keydown="handleKeyPress"
                                >
                                    <template #label
                                        ><span
                                            >{{ $translate("labels.name")
                                            }}<StitchHint
                                                :hint="
                                                    $translate(
                                                        'labels.nameHint'
                                                    )
                                                " /></span
                                    ></template>
                                </v-text-field>
                            </v-col>
                            <v-col v-if="!product.provider" cols="12" sm="6">
                                <v-text-field
                                    v-model="product.commercial_name"
                                    :error-messages="
                                        product_saving_errors.commercial_name
                                    "
                                    :label="$translate('labels.commercialName')"
                                    :placeholder="
                                        $translate(
                                            'labels.commercialNamePlaceholder'
                                        )
                                    "
                                    :disabled="product.archived || isOperator"
                                    dense
                                    hide-details="auto"
                                    outlined
                                    @keydown="handleKeyPress"
                                >
                                    <template #label
                                        ><span
                                            >{{
                                                $translate(
                                                    "labels.commercialName"
                                                )
                                            }}<StitchHint
                                                :hint="
                                                    $translate(
                                                        'labels.commercialNameHint'
                                                    )
                                                " /></span
                                    ></template>
                                </v-text-field>
                            </v-col>
                        </v-row>

                        <v-row class="mt-2">
                            <v-col
                                cols="12"
                                :sm="product.is_service && isOperator ? 12 : 6"
                            >
                                <v-text-field
                                    v-model="product.upc"
                                    :error-messages="product_saving_errors.upc"
                                    :label="$translate('labels.upc')"
                                    :placeholder="
                                        $translate('labels.upcPlaceholder')
                                    "
                                    :disabled="product.archived || isOperator"
                                    dense
                                    required
                                    hide-details="auto"
                                    outlined
                                    @keydown="handleKeyPress"
                                >
                                    <template #label
                                        ><span
                                            >{{ $translate("labels.upc")
                                            }}<StitchHint
                                                :hint="
                                                    $translate('labels.upcHint')
                                                " /></span
                                    ></template>
                                </v-text-field>
                            </v-col>
                            <v-col v-if="!product.is_service" cols="12" sm="6">
                                <v-text-field
                                    v-model="product.manufacturer"
                                    :error-messages="
                                        product_saving_errors.manufacturer
                                    "
                                    :label="$translate('labels.manufacturer')"
                                    :placeholder="
                                        $translate(
                                            'labels.manufacturerPlaceholder'
                                        )
                                    "
                                    :disabled="product.archived || isOperator"
                                    dense
                                    hide-details="auto"
                                    outlined
                                    @keydown="handleKeyPress"
                                >
                                    <template #label
                                        ><span
                                            >{{
                                                $translate(
                                                    "labels.manufacturer"
                                                )
                                            }}<StitchHint
                                                :hint="
                                                    $translate(
                                                        'labels.manufacturerHint'
                                                    )
                                                " /></span
                                    ></template>
                                </v-text-field>
                            </v-col>
                            <v-col
                                v-if="product.is_service && !isOperator"
                                cols="12"
                                sm="6"
                            >
                                <v-text-field
                                    :value="
                                        product.default_unit_purchase_price_excl_vat
                                    "
                                    @input="
                                        product.default_unit_purchase_price_excl_vat =
                                            normalizeDecimalString($event)
                                    "
                                    :error-messages="
                                        product_saving_errors.default_unit_purchase_price_excl_vat
                                    "
                                    :label="
                                        $translate(
                                            'labels.defaultPurchasePriceExclVat'
                                        )
                                    "
                                    :placeholder="
                                        $translate(
                                            'labels.defaultPurchasePriceExclVatPlaceholder'
                                        )
                                    "
                                    type="text"
                                    inputmode="decimal"
                                    :disabled="product.archived || isOperator"
                                    dense
                                    hide-details="auto"
                                    outlined
                                    @keydown="handleKeyPress"
                                >
                                    <template #label
                                        ><span
                                            >{{
                                                $translate(
                                                    "labels.defaultPurchasePriceExclVat"
                                                )
                                            }}<StitchHint
                                                :hint="
                                                    $translate(
                                                        'labels.defaultPurchasePriceExclVatHint'
                                                    )
                                                " /></span
                                    ></template>
                                </v-text-field>
                            </v-col>
                        </v-row>

                        <v-row
                            v-if="!isOperator && !product.is_service"
                            class="mt-2"
                        >
                            <v-col cols="12" sm="6">
                                <QuantityUnitInput
                                    v-model="product.packaging"
                                    :error-messages="
                                        product_saving_errors.packaging
                                    "
                                    :label="$translate('labels.packaging')"
                                    :placeholder="
                                        $translate(
                                            'labels.packagingPlaceholder'
                                        )
                                    "
                                    :disabled="product.archived || isOperator"
                                    hide-details="auto"
                                    outlined
                                    @keydown="handleKeyPress"
                                >
                                    <template #label
                                        ><span
                                            >{{ $translate("labels.packaging")
                                            }}<StitchHint
                                                :hint="
                                                    $translate(
                                                        'labels.packagingHint'
                                                    )
                                                " /></span
                                    ></template>
                                </QuantityUnitInput>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    :value="
                                        product.default_unit_purchase_price_excl_vat
                                    "
                                    @input="
                                        product.default_unit_purchase_price_excl_vat =
                                            normalizeDecimalString($event)
                                    "
                                    :error-messages="
                                        product_saving_errors.default_unit_purchase_price_excl_vat
                                    "
                                    :label="
                                        $translate(
                                            'labels.defaultPurchasePriceExclVat'
                                        )
                                    "
                                    :placeholder="
                                        $translate(
                                            'labels.defaultPurchasePriceExclVatPlaceholder'
                                        )
                                    "
                                    type="text"
                                    inputmode="decimal"
                                    :disabled="product.archived || isOperator"
                                    dense
                                    hide-details="auto"
                                    outlined
                                    @keydown="handleKeyPress"
                                >
                                    <template #label
                                        ><span
                                            >{{
                                                $translate(
                                                    "labels.defaultPurchasePriceExclVat"
                                                )
                                            }}<StitchHint
                                                :hint="
                                                    $translate(
                                                        'labels.defaultPurchasePriceExclVatHint'
                                                    )
                                                " /></span
                                    ></template>
                                </v-text-field>
                            </v-col>
                        </v-row>

                        <v-row
                            v-if="
                                !product.provider &&
                                !isOperator &&
                                !product.archived
                            "
                            class="mt-2"
                        >
                            <v-col cols="12">
                                <TagInput
                                    object-type="product"
                                    v-model="product.tags"
                                    :label="$translate('labels.tags')"
                                    :placeholder="
                                        $translate('labels.tagsPlaceholder')
                                    "
                                    :dense="true"
                                    :hide-details="true"
                                    outlined
                                    @keydown="handleKeyPress"
                                    :error-messages="
                                        product_saving_errors.tags
                                            ? [].concat(
                                                  product_saving_errors.tags
                                              )
                                            : []
                                    "
                                />
                            </v-col>
                        </v-row> </v-card-text
                ></v-card>
                <template v-if="!product.is_service">
                    <v-card class="stitch-modal__card" elevation="0"
                        ><v-card-text
                            ><h5 class="stitch-modal__title">
                                <v-icon size="16"
                                    >mdi-format-list-bulleted</v-icon
                                ><span>{{
                                    $translate("metadataSection")
                                }}</span>
                            </h5>
                            <ProductMetadata
                                class="product-metadata-fields"
                                :product="product"
                                :producttypes="producttypes || []"
                                :product-saving-errors="product_saving_errors"
                                :disabled-codes="formatLockedMetadataCodes"
                                :key="product.producttype || 'none'"
                                outlined
                                @producttype-changed="setProductType"
                                @metadata-changed="setMetadata"
                                @keydown="handleKeyPress"
                            /> </v-card-text></v-card
                ></template>

                <template v-if="isCustomer">
                    <v-card class="stitch-modal__card" elevation="0"
                        ><v-card-text
                            ><h5 class="stitch-modal__title">
                                <v-icon size="16">mdi-warehouse</v-icon
                                ><span>{{ $translate("stockSettings") }}</span>
                            </h5>
                            <v-row class="mt-0">
                                <v-col cols="12">
                                    <v-checkbox
                                        class="mt-0"
                                        :input-value="
                                            product.stock_tracking || false
                                        "
                                        :label="
                                            $translate('enableStockTracking')
                                        "
                                        :disabled="product.is_service"
                                        :error-messages="
                                            product_saving_errors.stock_tracking
                                                ? [].concat(
                                                      product_saving_errors.stock_tracking
                                                  )
                                                : []
                                        "
                                        hide-details="auto"
                                        single-line
                                        persistent-hint
                                        outlined
                                        @change="
                                            product.stock_tracking = $event;
                                            product.requires_stock_to_be_ordered = false;
                                            if (!$event) {
                                                product.low_stock_alert_treshold = 0;
                                                if (product.sale_unit == null) {
                                                    product.sale_unit =
                                                        product.stock_unit ||
                                                        'pc';
                                                }
                                            }
                                        "
                                    />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-select
                                        :disabled="
                                            product.archived ||
                                            isOperator ||
                                            product.is_service
                                        "
                                        v-model="product.stock_unit"
                                        :items="stockUnitOptions"
                                        :label="$translate('labels.stockUnit')"
                                        dense
                                        hide-details="auto"
                                        outlined
                                        @keydown="handleKeyPress"
                                    />
                                </v-col>
                                <v-col v-if="showFormatSelect" cols="12" sm="6">
                                    <FormatSelect
                                        :value="product.format"
                                        :formats="formatChoices"
                                        :label="$translate('labels.format')"
                                        :loading="formatsLoading"
                                        :disabled="
                                            product.archived ||
                                            isOperator ||
                                            product.is_service
                                        "
                                        clearable
                                        hide-details="auto"
                                        @input="onFormatChange"
                                        @keydown="handleKeyPress"
                                    />
                                </v-col>
                                <template
                                    v-if="(product.stock_unit || 'pc') === 'pc'"
                                >
                                    <v-col cols="12" sm="6">
                                        <v-text-field
                                            :value="
                                                product.piece_content_quantity
                                            "
                                            @input="
                                                product.piece_content_quantity =
                                                    normalizeDecimalString(
                                                        $event
                                                    )
                                            "
                                            :error-messages="
                                                product_saving_errors.piece_content_quantity
                                            "
                                            :label="
                                                $translate(
                                                    'labels.pieceContentQuantity'
                                                )
                                            "
                                            :disabled="
                                                product.archived ||
                                                isOperator ||
                                                product.is_service ||
                                                hasFormat
                                            "
                                            dense
                                            hide-details="auto"
                                            outlined
                                            type="text"
                                            inputmode="decimal"
                                            @keydown="handleKeyPress"
                                        >
                                            <template #label
                                                ><span
                                                    >{{
                                                        $translate(
                                                            "labels.pieceContentQuantity"
                                                        )
                                                    }}<StitchHint
                                                        :hint="
                                                            $translate(
                                                                'labels.pieceContentQuantityHint'
                                                            )
                                                        " /></span
                                            ></template>
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-select
                                            v-model="product.piece_content_unit"
                                            :error-messages="
                                                product_saving_errors.piece_content_unit
                                            "
                                            :items="pieceContentUnitOptions"
                                            :label="
                                                $translate(
                                                    'labels.pieceContentUnit'
                                                )
                                            "
                                            :disabled="
                                                product.archived ||
                                                isOperator ||
                                                product.is_service ||
                                                hasFormat
                                            "
                                            dense
                                            hide-details="auto"
                                            outlined
                                            @keydown="handleKeyPress"
                                        />
                                    </v-col>
                                    <v-col cols="12" class="pt-0 pb-0">
                                        <div
                                            class="text-caption grey--text text--darken-1"
                                        >
                                            {{
                                                $translate(
                                                    "pieceContentExplanation"
                                                )
                                            }}
                                        </div>
                                    </v-col>
                                </template>
                                <v-col
                                    v-if="product.stock_tracking"
                                    cols="12"
                                    sm="6"
                                >
                                    <v-text-field
                                        :disabled="
                                            product.archived ||
                                            isOperator ||
                                            product.is_service
                                        "
                                        :value="
                                            product.low_stock_alert_treshold ??
                                            0
                                        "
                                        @input="
                                            product.low_stock_alert_treshold =
                                                normalizeLowStockThreshold(
                                                    $event
                                                )
                                        "
                                        :error-messages="
                                            product_saving_errors.low_stock_alert_treshold
                                        "
                                        :label="
                                            $translate('labels.lowStockAlert')
                                        "
                                        :placeholder="
                                            $translate(
                                                'labels.lowStockAlertPlaceholder'
                                            )
                                        "
                                        dense
                                        hide-details="auto"
                                        outlined
                                        type="text"
                                        inputmode="decimal"
                                        @keydown="handleKeyPress"
                                    />
                                </v-col>
                            </v-row> </v-card-text
                    ></v-card>

                    <v-card class="stitch-modal__card" elevation="0"
                        ><v-card-text
                            ><h5 class="stitch-modal__title">
                                <v-icon size="16">mdi-cart-outline</v-icon
                                ><span>{{ $translate("sellSettings") }}</span>
                            </h5>
                            <v-checkbox
                                class="mt-0 mb-2"
                                dense
                                :input-value="
                                    product.available_for_sale || false
                                "
                                :label="$translate('availableForSale')"
                                hide-details
                                single-line
                                persistent-hint
                                outlined
                                @change="product.available_for_sale = $event"
                            />
                            <v-row v-if="product.available_for_sale">
                                <v-col cols="12" sm="6">
                                    <v-text-field
                                        :value="vatRate"
                                        @input="
                                            vatRate =
                                                normalizeDecimalString($event)
                                        "
                                        @focusout="saveVatRate"
                                        :error-messages="
                                            product_saving_errors.vat_rate
                                        "
                                        :label="
                                            $translate('labels.forceVatRate')
                                        "
                                        :placeholder="defaultVatRatePlaceholder"
                                        type="text"
                                        inputmode="decimal"
                                        :disabled="
                                            product.archived || isOperator
                                        "
                                        dense
                                        hide-details="auto"
                                        outlined
                                        @keydown="handleKeyPress"
                                    />
                                </v-col>
                            </v-row>
                            <v-row v-if="product.available_for_sale">
                                <v-col cols="12" sm="6">
                                    <v-select
                                        v-model="product.sale_unit"
                                        :items="stockUnitOptionsWithEmpty"
                                        :label="$translate('labels.saleUnit')"
                                        :disabled="
                                            product.archived ||
                                            isOperator ||
                                            product.is_service
                                        "
                                        dense
                                        hide-details="auto"
                                        outlined
                                        clearable
                                        @keydown="handleKeyPress"
                                    >
                                        <template #label
                                            ><span
                                                >{{
                                                    $translate(
                                                        "labels.saleUnit"
                                                    )
                                                }}<StitchHint
                                                    :hint="
                                                        $translate(
                                                            'labels.saleUnitHint'
                                                        )
                                                    " /></span
                                        ></template>
                                    </v-select>
                                </v-col>
                                <v-col
                                    v-if="showSaleFormatSelect"
                                    cols="12"
                                    sm="6"
                                >
                                    <FormatSelect
                                        :value="product.sale_format"
                                        :formats="compatibleSaleFormats"
                                        :label="$translate('labels.saleFormat')"
                                        :loading="formatsLoading"
                                        :disabled="
                                            product.archived ||
                                            isOperator ||
                                            product.is_service
                                        "
                                        clearable
                                        hide-details="auto"
                                        @input="onSaleFormatChange"
                                        @keydown="handleKeyPress"
                                    />
                                </v-col>
                                <template v-if="needsSaleUnitContent">
                                    <v-col cols="12" sm="6">
                                        <v-text-field
                                            :value="
                                                product.sale_unit_content_quantity
                                            "
                                            @input="
                                                product.sale_unit_content_quantity =
                                                    normalizeDecimalString(
                                                        $event
                                                    )
                                            "
                                            :error-messages="
                                                product_saving_errors.sale_unit_content_quantity
                                            "
                                            :label="
                                                $translate(
                                                    'labels.saleUnitContentQuantity'
                                                )
                                            "
                                            :disabled="
                                                product.archived ||
                                                isOperator ||
                                                product.is_service ||
                                                hasSaleFormat
                                            "
                                            dense
                                            hide-details="auto"
                                            outlined
                                            type="text"
                                            inputmode="decimal"
                                            @keydown="handleKeyPress"
                                        />
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-select
                                            v-model="
                                                product.sale_unit_content_unit
                                            "
                                            :error-messages="
                                                product_saving_errors.sale_unit_content_unit
                                            "
                                            :items="saleUnitContentUnitOptions"
                                            :label="
                                                $translate(
                                                    'labels.saleUnitContentUnit'
                                                )
                                            "
                                            :disabled="
                                                product.archived ||
                                                isOperator ||
                                                product.is_service ||
                                                hasSaleFormat
                                            "
                                            dense
                                            hide-details="auto"
                                            outlined
                                            @keydown="handleKeyPress"
                                        />
                                    </v-col>
                                </template>
                            </v-row>
                            <div class="product-sell-settings-options mt-2">
                                <v-checkbox
                                    class="mt-0 mb-0"
                                    dense
                                    v-model="
                                        product.requires_stock_to_be_ordered
                                    "
                                    :label="
                                        $translate('labels.requiresStockOrder')
                                    "
                                    hide-details
                                    single-line
                                    persistent-hint
                                    outlined
                                    :disabled="
                                        !product.available_for_sale ||
                                        !product.stock_tracking ||
                                        product.is_service
                                    "
                                    @change="
                                        product.requires_stock_to_be_ordered =
                                            $event
                                    "
                                />
                                <v-checkbox
                                    class="mt-0 mb-0"
                                    dense
                                    :input-value="
                                        product.requires_allocation || false
                                    "
                                    :label="
                                        $translate(
                                            'labels.requiresAllocationOrder'
                                        )
                                    "
                                    hide-details
                                    single-line
                                    persistent-hint
                                    outlined
                                    :disabled="
                                        !product.available_for_sale ||
                                        product.is_service
                                    "
                                    @change="
                                        product.requires_allocation = $event
                                    "
                                />
                            </div> </v-card-text
                    ></v-card>

                    <v-card
                        v-if="isNew"
                        class="stitch-modal__card"
                        elevation="0"
                        ><v-card-text
                            ><h5 class="stitch-modal__title">
                                <v-icon size="16">mdi-truck-outline</v-icon
                                ><span>{{
                                    $translate("suppliersSection")
                                }}</span>
                            </h5>
                            <v-row :key="JSON.stringify(product.providers)">
                                <v-col cols="12">
                                    <ProductProviders
                                        outlined
                                        class="product-provider-fields"
                                        :limit="product.provider ? 1 : null"
                                        :key="JSON.stringify(product.providers)"
                                        :value="product.providers || [{}]"
                                        :providers="providers"
                                        :editable="isCustomer"
                                        :allow-create-provider="
                                            userHasPermission(
                                                'core.add_provider'
                                            )
                                        "
                                        :errors="
                                            product_saving_errors.providers
                                        "
                                        @input="updateProductProviders"
                                        @provider-created="providerCreated"
                                    />
                                </v-col>
                            </v-row> </v-card-text
                    ></v-card>
                </template>
            </div>
        </template>
        <template slot="bottom">
            <template v-if="!product.archived">
                <Button
                    :text="$translate('cancel')"
                    @click="cancelEditClicked"
                    class="my-2 my-sm-4 w-100 w-sm-auto"
                    ghost
                />

                <Button
                    class="my-2 my-sm-4 w-100 w-sm-auto ml-sm-3"
                    :text="saveText"
                    :disabled="product_saving"
                    :loading="product_saving"
                    @click="saveInformationClicked"
                />
            </template>
            <template v-else>
                <Button
                    :text="$translate('close')"
                    @click="cancelEditClicked"
                    class="my-2 my-sm-4 w-100 w-sm-auto"
                    ghost
                />
            </template>
        </template>
    </ModalContent>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import Decimal from "decimal.js";
import axios from "@/plugins/axios";
import GlobalMixins from "@/mixins/global";
import ProductProviders from "@/components/common/productproviders";
import ProductMetadata from "@/components/common/product-metadata";
import AssignProductCatalogs from "@/modals/assign-product-catalogs.vue";
import ProductType from "@/modals/producttype";
import TagInput from "@/components/common/tag-input.vue";
import QuantityUnitInput from "@/components/common/quantity-unit-input";

export default {
    mixins: [GlobalMixins],
    components: { ProductProviders, ProductMetadata, TagInput, QuantityUnitInput },
    props: {
        owner: {
            type: Boolean,
            default: true,
        },
        params: {
            type: Object,
            required: true,
        },
    },

    computed: {
        ...mapState("session", ["stats", "settings"]),
        ...mapState("editproduct/producttypes", ["producttypes"]),
        ...mapState("editproduct", [
            "product",
            "product_saving",
            "product_saving_errors",
        ]),
        ...mapState("editproduct/providers", ["providers"]),
        hasFormat() {
            return this.product?.format != null;
        },
        hasSaleFormat() {
            return this.product?.sale_format != null;
        },
        showFormatSelect() {
            if (!this.product) {
                return false;
            }
            return (this.product.stock_unit || "pc") === "pc";
        },
        showSaleFormatSelect() {
            if (!this.product) {
                return false;
            }
            return (
                this.product.sale_unit != null
                && this.effectiveSaleUnit === "pc"
            );
        },
        compatibleSaleFormats() {
            const product = this.product;
            if (!product) {
                return this.formatChoices;
            }
            return (this.formatChoices || []).filter((fmt) =>
                this.$utils.formatCompatibleWithProduct(product, fmt),
            );
        },
        formatLockedMetadataCodes() {
            if (!this.product) {
                return [];
            }
            if (this.hasFormat) {
                const selected = this.formatChoices.find(
                    (item) => item.id === this.product.format,
                );
                const dimension = selected?.dimension
                    || this.product.format_data?.dimension;
                if (dimension === "capacity") {
                    return ["capacity"];
                }
                if (dimension === "weight") {
                    return ["weight"];
                }
                return ["capacity", "weight"];
            }
            const meta = this.pieceContentMetadata;
            if (meta) {
                return [meta.key];
            }
            return [];
        },
        pieceContentMetadata() {
            if (!this.product || this.hasFormat) {
                return null;
            }
            const quantity = this.product.piece_content_quantity;
            const unit = this.product.piece_content_unit;
            if (quantity == null || quantity === "" || !unit) {
                return null;
            }
            return this.$utils.contentToMetadataValue(quantity, unit);
        },
        vatRatePercent() {
            if (!this.product || this.product.vat_rate === null) {
                return null;
            }
            return new Decimal(this.product.vat_rate).times(100).toFixed(2);
        },
        saveText() {
            if (this.isNew) {
                return this.$translate("createProduct");
            }
            return this.$translate("save");
        },
        isNew() {
            return !this.params.product.id;
        },
        title() {
            return this.params.product.id
                ? this.$translate("editProduct")
                : this.$translate("createNewProduct");
        },
        productTypeChoices() {
            return [
                {
                    value: false,
                    title: this.$translate("labels.productGood"),
                    description: this.$translate("labels.productGoodDescription"),
                },
                {
                    value: true,
                    title: this.$translate("labels.productService"),
                    description: this.$translate("labels.productServiceDescription"),
                },
            ];
        },
        stockUnitOptions() {
            return this.$utils.getStockUnitOptions();
        },
        pieceContentUnitOptions() {
            const options = this.$utils.getPieceContentUnitOptions();
            // Selling a piece stock by weight/volume converts through the
            // piece content: only offer units of the sale unit dimension.
            const saleDim = this.crossDimensionSaleDimension;
            if (!saleDim) {
                return options;
            }
            return options.filter(
                (opt) => this.$utils.getUnitDimension(opt.value) === saleDim
            );
        },
        crossDimensionSaleDimension() {
            if (!this.product) {
                return null;
            }
            // Dimension ("weight"/"volume") of the sale unit when it differs
            // from a piece stock unit, null otherwise.
            const stockUnit = this.product.stock_unit || 'pc';
            if (stockUnit !== 'pc') {
                return null;
            }
            const saleDim = this.$utils.getUnitDimension(this.effectiveSaleUnit);
            return saleDim === 'piece' ? null : saleDim;
        },
        stockUnitOptionsWithEmpty() {
            return [
                { text: this.$translate('labels.sameAsStockUnit'), value: null },
                ...this.stockUnitOptions,
            ];
        },
        saleUnitContentUnitOptions() {
            if (!this.product) {
                return [];
            }
            const stockUnit = this.product.stock_unit || 'pc';
            if (stockUnit === 'pc') {
                return [{ text: 'pc', value: 'pc' }, ...this.pieceContentUnitOptions];
            }
            return this.stockUnitOptions.filter((opt) => opt.value !== 'pc');
        },
        effectiveSaleUnit() {
            if (!this.product) {
                return 'pc';
            }
            return this.product.sale_unit || this.product.stock_unit || 'pc';
        },
        needsSaleUnitContent() {
            if (!this.product) {
                return false;
            }
            const saleUnit = this.effectiveSaleUnit;
            const stockUnit = this.product.stock_unit || 'pc';
            if (saleUnit === stockUnit) {
                // Same unit on both sides (e.g. pc/pc): one sale unit is one
                // stock unit, nothing to convert. Portions like "12 cl glass"
                // are handled by sale formats on catalog entries.
                return false;
            }
            const dimensions = {
                pc: 'piece',
                kg: 'weight',
                g: 'weight',
                L: 'volume',
                cl: 'volume',
                ml: 'volume',
            };
            const saleDim = dimensions[saleUnit];
            const stockDim = dimensions[stockUnit];
            if (saleDim && stockDim && saleDim === stockDim) {
                return false;
            }
            // Content per sale unit only makes sense for a piece-like sale
            // unit (e.g. a "crate" of 6 kg). Selling a piece stock by weight
            // or volume converts through the piece content instead.
            return saleUnit === 'pc';
        },
        defaultVatRatePlaceholder() {
            const rate = this.current_entity_settings?.default_vat_rate;
            if (rate === null || rate === undefined) {
                return null;
            }
            return `${new Decimal(rate).times(100).toFixed(2)} %`;
        },
        canAssignSalesCatalogs() {
            return (
                this.userHasPermission("catalog.add_catalogentry") &&
                this.userHasPermission("catalog.change_catalogentry")
            );
        },
    },
    beforeCreate() {
        // Seed the store before computed properties and watchers initialize:
        // props are not mounted yet, but propsData is available.
        const params = this.$options.propsData?.params;
        if (!params) {
            return;
        }
        this.$store.dispatch('editproduct/init', {
            product: { is_service: false, ...(params.product || {}) },
        });
    },
    watch: {
        'product.is_service'(newValue) {
            if (!this.product) {
                return;
            }
            if (newValue) {
                this.clearServiceIncompatibleFields();
            } else {
                this.setProductDefaults();
            }
        },
        needsSaleUnitContent(value) {
            // Clear hidden sale content when a unit change makes it
            // irrelevant (e.g. sale and stock units both become "pc").
            if (!value && this.product) {
                this.product.sale_unit_content_quantity = null;
                this.product.sale_unit_content_unit = null;
            }
        },
        crossDimensionSaleDimension(saleDim) {
            if (!this.product) {
                return;
            }
            // Selling a piece stock by weight/volume requires a piece content
            // of the same dimension: drop a now-incompatible unit so the user
            // picks one from the filtered options.
            const unit = this.product.piece_content_unit;
            if (
                saleDim &&
                unit &&
                this.$utils.getUnitDimension(unit) !== saleDim
            ) {
                this.product.piece_content_unit = null;
            }
        },
        'product.stock_unit'(newValue, oldValue) {
            if (!this.product) {
                return;
            }
            if (newValue === 'pc') {
                return;
            }
            this.product.piece_content_quantity = null;
            this.product.piece_content_unit = null;
            if (this.product.format) {
                this.onFormatChange(null);
            }
        },
        showFormatSelect(newValue) {
            if (!newValue && this.product?.format) {
                this.onFormatChange(null);
            }
        },
        showSaleFormatSelect(newValue) {
            if (!newValue && this.product?.sale_format) {
                this.onSaleFormatChange(null);
            }
        },
        'product.sale_unit'(newValue) {
            if (newValue != null && newValue !== "pc" && this.product.sale_format) {
                this.onSaleFormatChange(null);
            }
        },
        pieceContentMetadata: {
            handler(meta) {
                if (!this.product || this.hasFormat || !meta) {
                    return;
                }
                this.applyContentToMetadata(
                    this.product.piece_content_quantity,
                    this.product.piece_content_unit,
                );
            },
            immediate: true,
        },
    },
    data() {
        return {
            vatRate: this.params.product?.vat_rate
                ? new Decimal(this.params.product?.vat_rate).times(100).toString()
                : null,
            formatsLoading: false,
            formatChoices: [],
        };
    },
    async mounted() {
        await this.fetchFormats();
        if (this.isNew) {
            if (
                this.showFormatSelect
                && !this.product.format
                && this.settings?.default_format
            ) {
                this.product.format = this.settings.default_format;
            }
            if (this.product.format && this.showFormatSelect) {
                this.onFormatChange(this.product.format);
            }
        }
    },
    methods: {
        ...mapMutations("editproduct", [
            "updatePurchasePrices",
            "updateProductType",
            "updateProductProviders",
        ]),
        ...mapActions("editproduct", ["saveProduct"]),
        ...mapActions("editproduct/providers", ["fetchProviders"]),
        ...mapActions("editproduct/producttypes", ["fetchProductTypes"]),
        normalizeDecimalString(value) {
            return this.$utils.normalizeDecimalString(value);
        },
        normalizeLowStockThreshold(value) {
            return this.$utils.normalizeLowStockThreshold(value);
        },
        applyContentToMetadata(contentQuantity, contentUnit) {
            const meta = this.$utils.contentToMetadataValue(
                contentQuantity,
                contentUnit,
            );
            if (!meta) {
                return;
            }
            if (!this.product.metadata) {
                this.product.metadata = {};
            }
            this.$set(this.product.metadata, meta.key, meta.value);
        },
        async fetchFormats() {
            this.formatsLoading = true;
            try {
                const response = await axios.get("/api/formats/", {
                    params: { limit: 10000 },
                });
                this.formatChoices = response.data.results || response.data || [];
            } catch (error) {
                console.error("Error loading formats:", error);
                this.formatChoices = [];
            } finally {
                this.formatsLoading = false;
            }
        },
        onFormatChange(formatId) {
            if (!formatId) {
                this.product.format = null;
                this.product.format_data = null;
                return;
            }
            const format = this.formatChoices.find((item) => item.id === formatId);
            if (!format) {
                return;
            }
            this.product.format = format.id;
            this.product.format_data = format;
            if ((this.product.stock_unit || "pc") === "pc") {
                this.product.piece_content_quantity = this.$utils.formatDecimalInputValue(
                    format.content_quantity,
                    3,
                );
                this.product.piece_content_unit = format.content_unit;
            }
            this.applyContentToMetadata(
                format.content_quantity,
                format.content_unit,
            );
        },
        onSaleFormatChange(formatId) {
            if (!formatId) {
                this.product.sale_format = null;
                this.product.sale_format_data = null;
                return;
            }
            const format = this.formatChoices.find((item) => item.id === formatId);
            if (!format) {
                return;
            }
            this.product.sale_format = format.id;
            this.product.sale_format_data = format;
            // Sale format labels the sale piece; it must not rewrite stock piece content.
            if (
                format.content_quantity != null
                && format.content_unit
                && this.needsSaleUnitContent
            ) {
                this.product.sale_unit_content_quantity =
                    this.$utils.formatDecimalInputValue(format.content_quantity, 3);
                this.product.sale_unit_content_unit = format.content_unit;
            }
        },
        saveVatRate() {
            if (this.vatRate == "" || this.vatRate === null) {
                this.product.vat_rate = null;
            } else {
                try {
                    this.product.vat_rate = new Decimal(this.vatRate)
                        .dividedBy(100)
                        .toString();
                } catch (e) {
                    this.product.vat_rate = null;
                }
            }
        },
        providerCreated(provider) {
            this.fetchProviders();
        },

        async setProductType(producttype) {
            if (producttype === "new") {
                if (!this.userHasPermission("core.add_producttype")) {
                    return;
                }
                const result = await this.showModal({
                    component: ProductType,
                    params: {
                        producttype: {},
                    },
                });
                if (result?.id) {
                    await this.fetchProductTypes();
                    this.updateProductType(result.id);
                }
                this.$forceUpdate();
                return;
            }
            this.updateProductType(
                producttype === undefined || producttype === ""
                    ? null
                    : producttype
            );
            this.$forceUpdate();
        },

        cancelEditClicked() {
            this.hideModal();
        },
        
        // Handle Enter key for automatic save
        handleKeyPress(event) {
            if (event.key === 'Enter') {
                event.preventDefault();
                
                // Check if we can save
                if (!this.product.archived && !this.product_saving) {
                    this.saveInformationClicked();
                }
            }
        },
        
        async saveInformationClicked() {
            if (this.product_saving) return;
            try {
                const response = await this.saveProduct(this.params.inventoryCreation ? { inventoryCreation: this.params.inventoryCreation } : undefined);
                const savedProduct = response.data;
                const isCreated = response.status === 201;

                this.addAlert(
                    this.$translate(isCreated ? "productCreated" : "productSaved")
                );


                if (
                    isCreated &&
                    !this.params.inventoryCreation &&
                    savedProduct?.id &&
                    (savedProduct.available_for_sale ?? true) &&
                    this.canAssignSalesCatalogs
                ) {
                    const initialSelection = Array.isArray(savedProduct?.catalogs)
                        ? savedProduct.catalogs
                              .map((catalog) =>
                                  typeof catalog === "object" ? catalog.id : catalog
                              )
                              .filter(Boolean)
                        : [];

                    await this.showModal({
                        component: AssignProductCatalogs,
                        params: {
                            product: savedProduct,
                            initialSelection,
                        },
                    });
                }
                this.hideModal({ result: savedProduct });

            } catch (error) {
                this.$swal({
                    title: this.$translate("failedSavingProduct"),
                    html: this.$utils.resultToSwalContent(this, error),
                    type: "error",
                });
            }
        },
        setMetadata({ code, value }) {
            this.product.metadata[code] = value;
        },
        clearServiceIncompatibleFields() {
            // Clear fields that are not applicable to services
            this.product.manufacturer = null;
            this.product.packaging = 1;
            this.product.producttype = null;
            
            // Clear stock-related fields
            this.product.stock_tracking = false;
            this.product.low_stock_alert_treshold = 0;
            this.product.requires_stock_to_be_ordered = false;
            this.product.requires_allocation = false;
        },
        setProductDefaults() {
            this.product.stock_tracking = true;
            this.product.low_stock_alert_treshold = 0;
            this.product.requires_stock_to_be_ordered = false;
            this.product.requires_allocation = false;
        },
    },
    i18n: {
        messages: {
            en: {
                stockSettings: "Stock settings",
                sellSettings: "Sell settings",
                cancel: "Cancel",
                close: "Close",
                createProduct: "Create product",
                save: "Save",
                editProduct: "Edit product",
                createNewProduct: "Create new product",
                productCreated: "Product created",
                productSaved: "Product saved",
                failedSavingProduct: "Failed saving product",
                availableForSale: "Available for sale",
                enableStockTracking: "Enable stock tracking",
                archivedProduct: "This product has been archived and cannot be modified.",
                productNotOwned:
                    "The product information can only be edited by the client, but you can edit the stock-related information.",
                productInformations: "Product information",
                metadataSection: "Characteristics",
                suppliersSection: "Suppliers and purchase prices",
                labels: {
                    name: "Name",
                    namePlaceholder: "Product name",
                    nameHint:
                        "Internal reference name used in inventory and deliveries.",
                    commercialName: "Commercial name",
                    commercialNamePlaceholder: "Customer-facing name",
                    commercialNameHint:
                        "Used as the catalog entry label and POS button label. Defaults to the product name.",
                    upc: "UPC",
                    upcPlaceholder: "Unique product reference",
                    upcHint: "Automatically generated if not provided.",
                    manufacturer: "Manufacturer",
                    manufacturerPlaceholder: "Manufacturer name",
                    manufacturerHint:
                        "Product manufacturer; not necessarily the same as your supplier.",
                    packaging: "Packaging",
                    packagingPlaceholder: "Number of units per package",
                    productType: "Product type",
                    productGood: "Good",
                    productGoodDescription:
                        "Physical product whose stock can be tracked or shipped (wine, food, equipment…).",
                    productService: "Service",
                    productServiceDescription:
                        "Intangible item without stock (delivery fee, consulting, service…).",
                    forceVatRate: "Force VAT rate (%)",
                    defaultPurchasePriceExclVat: "Default purchase price (excl. VAT)",
                    defaultPurchasePriceExclVatPlaceholder: "Default purchase price (excl. VAT)",
                    defaultPurchasePriceExclVatHint:
                        "Reference purchase price used by default on receipts and provider invoices.",
                    packagingHint:
                        "Number of units per package when ordering from a supplier (e.g. 6 for a case of 6 bottles).",
                    lowStockAlert: "Low stock alert",
                    lowStockAlertPlaceholder: "Low stock alert threshold",
                    format: "Format",
                    stockUnit: "Stock unit",
                    pieceContentQuantity: "Content per piece",
                    pieceContentQuantityHint: "Weight or volume of one piece (e.g. 75 cl for a bottle)",
                    pieceContentUnit: "Content unit",
                    sameAsStockUnit: "Same as stock unit",
                    saleUnit: "Sale unit",
                    saleUnitHint: "Unit used on orders and invoices (defaults to stock unit)",
                    saleFormat: "Sale format",
                    saleUnitAllowDecimal: "Allow decimal sale quantities",
                    saleUnitContentQuantity: "Content per sale unit",
                    saleUnitContentUnit: "Sale unit content unit",
                    requiresStockOrder: "Requires stock for being ordered",
                    requiresAllocationOrder: "Requires allocation for being ordered",
                    tags: "Tags",
                    tagsPlaceholder: "Add tags to categorize this product...",
                },
                noWarehouse:
                    "No warehouse is configured. Please create one to enable stock tracking on this product.",
                manageWarehouse: "Manage warehouses",
                pieceContentExplanation:
                    "Optional: the content unit and quantity make stock counting easier (e.g. counting in cl or g) and allow selling this product in different formats (e.g. a 15 cl glass out of a 75 cl bottle). They are only required when the sale unit differs from the stock unit.",
            },
            fr: {
                stockSettings: "Paramètres de stock",
                sellSettings: "Paramètres de vente",
                cancel: "Annuler",
                close: "Fermer",
                createProduct: "Créer le produit",
                save: "Enregistrer",
                editProduct: "Éditer le produit",
                createNewProduct: "Créer un nouveau produit",
                productCreated: "Produit créé",
                productSaved: "Produit sauvegardé",
                failedSavingProduct: "Impossible de sauvegarder le produit",
                availableForSale: "Disponible à la vente",
                enableStockTracking: "Activer le suivi de stock",
                archivedProduct: "Ce produit a été archivé et ne peut pas être modifié.",
                productNotOwned:
                    "Les informations du produit ne peuvent être modifiées que par le client, mais vous pouvez modifier les informations liées au stock.",
                productInformations: "Informations sur le produit",
                metadataSection: "Caractéristiques",
                suppliersSection: "Fournisseurs et prix d’achat",
                labels: {
                    name: "Nom",
                    namePlaceholder: "Nom du produit",
                    nameHint:
                        "Nom de référence interne utilisé dans l'inventaire et les expéditions.",
                    commercialName: "Nom commercial",
                    commercialNamePlaceholder: "Nom affiché aux clients",
                    commercialNameHint:
                        "Utilisé comme libellé des entrées de catalogue et du bouton de caisse. Par défaut : nom du produit.",
                    upc: "UPC",
                    upcPlaceholder: "Référence unique du produit",
                    upcHint: "Généré automatiquement si non fourni.",
                    manufacturer: "Fabricant",
                    manufacturerPlaceholder: "Nom du fabricant",
                    manufacturerHint:
                        "Fabricant du produit, pas nécessairement le fournisseur.",
                    packaging: "Conditionnement",
                    packagingPlaceholder: "Nombre d'unités par emballage",
                    productType: "Type de produit",
                    productGood: "Bien",
                    productGoodDescription:
                        "Produit physique dont le stock peut être suivi ou expédié (vin, alimentaire, matériel…).",
                    productService: "Service",
                    productServiceDescription:
                        "Prestation sans stock (frais de livraison, conseil, service…).",
                    forceVatRate: "Forcer le taux de TVA (%)",
                    defaultPurchasePriceExclVat: "Prix d'achat par défaut (HT)",
                    defaultPurchasePriceExclVatPlaceholder: "Prix d'achat par défaut (HT)",
                    defaultPurchasePriceExclVatHint:
                        "Prix d'achat de référence utilisé par défaut sur les réceptions et factures fournisseur.",
                    packagingHint:
                        "Nombre d'unités par emballage lors des commandes fournisseur (ex. 6 pour un carton de 6 bouteilles).",
                    lowStockAlert: "Alerte stock bas",
                    lowStockAlertPlaceholder: "Seuil d'alerte stock bas",
                    format: "Format",
                    stockUnit: "Unité de stock",
                    pieceContentQuantity: "Contenance par pièce",
                    pieceContentQuantityHint: "Poids ou volume d'une pièce (ex. 75 cl pour une bouteille)",
                    pieceContentUnit: "Unité de contenance",
                    sameAsStockUnit: "Identique à l'unité de stock",
                    saleUnit: "Unité de vente",
                    saleUnitHint: "Unité utilisée sur les commandes et factures (par défaut : unité de stock)",
                    saleFormat: "Format de vente",
                    saleUnitAllowDecimal: "Autoriser les quantités décimales à la vente",
                    saleUnitContentQuantity: "Contenu par unité de vente",
                    saleUnitContentUnit: "Unité de contenu de vente",
                    requiresStockOrder: "Nécessite du stock pour être commandé",
                    requiresAllocationOrder: "Nécessite une allocation pour être commandé",
                    tags: "Tags",
                    tagsPlaceholder: "Ajoutez des tags pour catégoriser ce produit...",
                },
                noWarehouse:
                    "Aucun entrepôt n'est configuré. Veuillez en créer un pour activer le suivi des stocks sur ce produit.",
                manageWarehouse: "Gérer les entrepôts",
                pieceContentExplanation:
                    "Optionnel : l'unité et la contenance facilitent les comptages de stock (ex. saisie en cl ou en g) et permettent de vendre ce produit dans différents formats (ex. un verre de 15 cl issu d'une bouteille de 75 cl). Elles ne sont obligatoires que si l'unité de vente diffère de l'unité de stock.",
            },
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/_stitch-modal.scss";
@import "@/assets/css/_settings-reference.scss";

.product-form ::v-deep .v-text-field--outlined { margin-top: 22px; }
.product-form ::v-deep .v-text-field--outlined .v-label {
    top: -23px;
    left: 0 !important;
    transform: none;
    max-width: 100%;
    font-size: 11px;
    font-weight: 600;
    line-height: 16px;
    letter-spacing: .06em;
    text-transform: uppercase;
}
.product-form ::v-deep .v-text-field--outlined legend { width: 0 !important; max-width: 0; padding: 0; }
.product-metadata-fields ::v-deep > h6,
.product-provider-fields ::v-deep > .text-overline { display: none; }
.product-form ::v-deep .row { margin: -8px; }
.product-form ::v-deep .row > [class*="col-"] { padding: 8px; }

.product-type-field__label {
    font-size: 12px;
    color: $color-gs-medium;
}

.product-type-option {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    height: 100%;
    min-height: 100%;
    padding: 12px 14px;
    border: 1px solid rgba(0, 0, 0, 0.24);
    border-radius: 4px;
    background: #fff;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease;

    &:hover:not(:disabled) {
        border-color: rgba(0, 0, 0, 0.42);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }

    &--selected {
        border-color: var(--v-primary-base, #1976d2);
        background: rgba(25, 118, 210, 0.04);
    }
}

.product-type-option__title {
    font-size: 14px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.87);
    line-height: 1.3;
}

.product-type-option__description {
    margin-top: 4px;
    font-size: 12px;
    color: $color-gs-medium;
    line-height: 1.4;
}

.product-field-hint {
    font-size: 12px;
    color: $color-gs-medium;
    line-height: 1.4;
}

.product-sell-settings-options {
    display: flex;
    flex-direction: column;
    gap: 0;

    ::v-deep .v-input--selection-controls {
        margin-top: 0;
        padding-top: 0;
        padding-bottom: 0;
        min-height: 36px;
    }

    ::v-deep .v-input--selection-controls:not(:first-child) {
        margin-top: -4px;
    }
}
</style>
